import { Button } from '@unified-lk/ui'
import { formatDate } from '../../lib/format'
import type { StaffPatientMedicalCardsData } from '../../types/plans'
import {
  workspaceActionsClassName,
  workspaceCardClassName,
  workspaceDetailGridClassName,
  workspaceEmptyClassName,
  workspaceFieldClassName,
  workspaceFieldLabelClassName,
  workspaceGridClassName,
  workspaceHeaderClassName,
  workspaceInputClassName,
  workspaceKickerClassName,
  workspaceMetricClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
  workspaceSectionHeaderClassName,
  workspaceSummaryGridClassName,
  workspaceTableCardRowClassName,
  workspaceTableClassName,
} from '../workspaceStyles'

export function PatientMedicalCardsSection({
  data,
  loading,
  error,
  filterDraft,
  onFilterDraftChange,
  onApplyFilters,
  onResetFilters,
  onSelectPatient,
  onSelectRecord,
  onClearSelection,
  onExport,
  onRetry,
}: {
  data: StaffPatientMedicalCardsData | null
  loading: boolean
  error: string | null
  filterDraft: {
    fromDate: string
    toDate: string
    doctorId: string
  }
  onFilterDraftChange: (patch: Partial<{ fromDate: string; toDate: string; doctorId: string }>) => void
  onApplyFilters: () => void
  onResetFilters: () => void
  onSelectPatient: (patientId: number) => void
  onSelectRecord: (recordId: number, patientId: number) => void
  onClearSelection: () => void
  onExport: () => void
  onRetry: () => void
}) {
  if (loading && !data) {
    return <div className={workspaceEmptyClassName}><p>Загружаем медицинские карты...</p></div>
  }

  if (!data) {
    return <div className={workspaceEmptyClassName}><p>{error ?? 'Раздел медкарт пока недоступен.'}</p></div>
  }

  return (
    <div className={workspaceGridClassName}>
      <section className={workspacePanelClassName}>
        <div className={workspaceHeaderClassName}>
          <div className="grid gap-2">
            <p className={workspaceKickerClassName}>Medical Cards</p>
            <h2 className="text-2xl font-semibold text-slate-900">Медицинские карты</h2>
            <p className={workspaceMutedTextClassName}>
              Журнал клинических записей с фильтрами, drilldown по пациенту и параметрам выбранной записи.
            </p>
            {data.sources ? <p className="text-xs leading-5 text-slate-500">Source: `{data.sources.medical_cards}`</p> : null}
          </div>
          <div className={workspaceActionsClassName}>
            <Button variant="secondary" type="button" onClick={onExport}>Экспорт CSV</Button>
            <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
          </div>
        </div>

        <div className={workspaceSummaryGridClassName}>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Записей</span><strong className="text-slate-900">{data.summary.records}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Пациентов</span><strong className="text-slate-900">{data.summary.patients}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Врачей</span><strong className="text-slate-900">{data.summary.doctors}</strong></div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[repeat(3,minmax(0,1fr))_auto] xl:items-end">
          <label className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>От даты</span>
            <input className={workspaceInputClassName} type="date" value={filterDraft.fromDate} onChange={(event) => onFilterDraftChange({ fromDate: event.target.value })} />
          </label>
          <label className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>До даты</span>
            <input className={workspaceInputClassName} type="date" value={filterDraft.toDate} onChange={(event) => onFilterDraftChange({ toDate: event.target.value })} />
          </label>
          <label className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Врач</span>
            <select className={workspaceInputClassName} value={filterDraft.doctorId} onChange={(event) => onFilterDraftChange({ doctorId: event.target.value })}>
              <option value="">Любой врач</option>
              {data.doctors.filter((doctor) => doctor.doctor_id !== null).map((doctor) => (
                <option key={doctor.doctor_id} value={String(doctor.doctor_id)}>
                  {doctor.doctor_name} ({doctor.record_count})
                </option>
              ))}
            </select>
          </label>
          <div className={workspaceActionsClassName}>
            <Button type="button" onClick={onApplyFilters}>Применить</Button>
            <Button variant="secondary" type="button" onClick={onResetFilters}>Сбросить</Button>
            {(data.selected_patient || data.selected_record) ? (
              <Button variant="secondary" type="button" onClick={onClearSelection}>Сбросить drilldown</Button>
            ) : null}
          </div>
        </div>
      </section>

      <section className={workspacePanelClassName}>
        <div className={workspaceSectionHeaderClassName}>
          <h3 className="text-xl font-semibold text-slate-900">Статистика по врачам</h3>
          <span className="text-sm text-slate-500">{data.doctors.length} врачей в текущем окне</span>
        </div>
        <div className="grid gap-4 xl:grid-cols-3">
          {data.doctors.map((doctor) => (
            <article key={`${doctor.doctor_id ?? 'na'}-${doctor.doctor_name}`} className={workspaceCardClassName}>
              <div className={workspaceSectionHeaderClassName}>
                <h3 className="text-lg font-semibold text-slate-900">{doctor.doctor_name}</h3>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">{doctor.share_percent}%</span>
              </div>
              <p className={workspaceMutedTextClassName}>Записей: {doctor.record_count}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={workspacePanelClassName}>
        {error ? <div className="rounded-[20px] bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700"><p>{error}</p></div> : null}
        <div className={workspaceTableClassName}>
          <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_140px_120px_minmax(0,1.2fr)_220px] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 lg:grid">
            <span>Пациент</span>
            <span>Врач</span>
            <span>Дата</span>
            <span>Тип</span>
            <span>Сводка</span>
            <span>Действия</span>
          </div>
          {data.records.map((record) => (
            <div key={record.record_id} className={`${workspaceTableCardRowClassName} lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_140px_120px_minmax(0,1.2fr)_220px]`}>
              <span>
                <strong className="block text-slate-900">{record.patient_name}</strong>
                <small className="text-slate-500">#{record.record_id}</small>
              </span>
              <span>{record.doctor_name}</span>
              <span>{formatDate(record.record_date)}</span>
              <span>{record.record_type}</span>
              <span>{record.summary}</span>
              <span>
                <div className={workspaceActionsClassName}>
                  <Button variant="secondary" type="button" onClick={() => onSelectPatient(record.patient_id)}>Пациент</Button>
                  <Button type="button" onClick={() => onSelectRecord(record.record_id, record.patient_id)}>Параметры</Button>
                </div>
              </span>
            </div>
          ))}
        </div>
        {data.records.length === 0 ? <div className={workspaceEmptyClassName}><p>В выбранном окне записи не найдены.</p></div> : null}
      </section>

      <section className={workspaceDetailGridClassName}>
        <article className={workspacePanelClassName}>
          <div className={workspaceSectionHeaderClassName}>
            <h3 className="text-xl font-semibold text-slate-900">Записи пациента</h3>
            <span className="text-sm text-slate-500">{data.selected_patient ? data.selected_patient.patient_name : 'Пациент не выбран'}</span>
          </div>
          {!data.selected_patient ? (
            <div className={workspaceEmptyClassName}><p>Выберите пациента из списка, чтобы открыть его записи.</p></div>
          ) : (
            <div className={workspaceGridClassName}>
              {data.selected_patient.records.map((record) => (
                <article key={record.record_id} className={workspaceCardClassName}>
                  <div className={workspaceSectionHeaderClassName}>
                    <strong className="text-slate-900">{formatDate(record.record_date)}</strong>
                    <span className="text-sm text-slate-500">{record.record_type}</span>
                  </div>
                  <p className={workspaceMutedTextClassName}>{record.summary}</p>
                  <div className="text-sm text-slate-600">{record.doctor_name}</div>
                  <div className={workspaceActionsClassName}>
                    <Button type="button" onClick={() => onSelectRecord(record.record_id, record.patient_id)}>Открыть запись</Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </article>

        <article className={workspacePanelClassName}>
          <div className={workspaceSectionHeaderClassName}>
            <h3 className="text-xl font-semibold text-slate-900">Детали записи</h3>
            <span className="text-sm text-slate-500">{data.selected_record ? `#${data.selected_record.record_id}` : 'Запись не выбрана'}</span>
          </div>
          {!data.selected_record ? (
            <div className={workspaceEmptyClassName}><p>Выберите запись, чтобы увидеть детали и параметры.</p></div>
          ) : (
            <>
              <ul className="grid gap-3 md:grid-cols-2 p-0">
                <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><strong className="block text-slate-900">Пациент</strong><span className="text-sm text-slate-600">{data.selected_record.patient_name}</span></li>
                <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><strong className="block text-slate-900">Врач</strong><span className="text-sm text-slate-600">{data.selected_record.doctor_name}</span></li>
                <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><strong className="block text-slate-900">Дата</strong><span className="text-sm text-slate-600">{formatDate(data.selected_record.record_date)}</span></li>
                <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><strong className="block text-slate-900">Тип</strong><span className="text-sm text-slate-600">{data.selected_record.record_type}</span></li>
              </ul>
              <p className={workspaceMutedTextClassName}>{data.selected_record.comment || data.selected_record.summary}</p>

              <div className={workspaceTableClassName}>
                <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 lg:grid">
                  <span>Параметр</span>
                  <span>Значение</span>
                </div>
                {data.selected_record.params.map((param) => (
                  <div key={param.key} className={`${workspaceTableCardRowClassName} lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]`}>
                    <span>{param.label}</span>
                    <span>{param.value}</span>
                  </div>
                ))}
              </div>
              {data.selected_record.params.length === 0 ? <div className={workspaceEmptyClassName}><p>Для выбранной записи параметры не пришли.</p></div> : null}
            </>
          )}
        </article>
      </section>
    </div>
  )
}
